import * as React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, GitMerge, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  getApiErrorReason,
  isMergeWorkActive,
  useCloseMergeRequestMutation,
  useListMergeRequestsQuery,
  type MergeRequestListItem,
  type MergeRequestStatus
} from '@axilon-apps/domains-ontology-ui-state'
import { formatRelativeTime } from '../../lib/time'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select'

type StatusFilter = MergeRequestStatus | 'all'

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'merged', label: 'Merged' },
  { value: 'closed', label: 'Closed' },
  { value: 'all', label: 'All' }
]

const STATUS_BADGE: Record<string, string> = {
  open: 'bg-brand-soft text-brand',
  merged: 'bg-st-ok-bg text-st-ok',
  closed: 'bg-st-idle-bg/60 text-ink-3',
  'Preview queued': 'bg-brand-soft text-brand',
  Previewing: 'bg-brand-soft text-brand',
  'Ready to merge': 'bg-brand-soft text-brand',
  'Merge queued': 'bg-brand-soft text-brand',
  Merging: 'bg-brand-soft text-brand',
  Failed: 'bg-st-down-bg text-st-down',
  Merged: 'bg-st-ok-bg text-st-ok',
  Closed: 'bg-st-idle-bg/60 text-ink-3'
}

/** Operation-specific label for a list row (lifecycle + background work). */
export function mergeRequestStatusLabel(request: MergeRequestListItem): string {
  if (request.status === 'merged') return 'Merged'
  if (request.status === 'closed') return 'Closed'
  if (request.workStatus === 'failed') return 'Failed'
  if (request.workKind === 'preview' && request.workStatus === 'queued') return 'Preview queued'
  if (request.workKind === 'preview' && request.workStatus === 'running') return 'Previewing'
  if (request.workKind === 'apply' && request.workStatus === 'queued') return 'Merge queued'
  if (request.workKind === 'apply' && request.workStatus === 'running') return 'Merging'
  if (request.status === 'open') return 'Ready to merge'
  return request.status
}

/**
 * Merges in progress (`/branches/merges`): the durable merge sessions, so a
 * review can be resumed — by its author or a teammate — instead of re-resolved
 * from scratch. Merge wake-ups on the event stream refetch the list.
 */
export default function MergesPage() {
  const [status, setStatus] = React.useState<StatusFilter>('open')
  const { data, isLoading, isError, refetch } = useListMergeRequestsQuery(
    status === 'all' ? {} : { status }
  )
  const requests = data ?? []

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-card">
      <header>
        <Link
          to="/settings?tab=branches"
          className="mb-2 inline-flex items-center gap-1 text-xs text-ink-3 hover:text-ink"
        >
          <ArrowLeft className="size-3.5" />
          Branches
        </Link>
        <div className="flex items-center justify-between gap-2">
          <h1 className="m-0 text-2xl font-semibold text-ink">Merges in progress</h1>
          <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
            <SelectTrigger size="sm" aria-label="Filter by status" className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="mt-1 text-sm text-ink-3">
          In-progress merge reviews. Resume one to pick up where it was left off.
        </p>
      </header>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center gap-2 py-6 text-sm text-ink-3">
            <Loader2 className="size-4 animate-spin" />
            Loading merges…
          </CardContent>
        </Card>
      ) : isError ? (
        <Card>
          <CardContent className="flex items-center justify-between gap-2 py-6 text-sm text-st-down">
            Could not load merges.
            <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-sm text-ink-3">
            No {status === 'all' ? '' : status} merges.{' '}
            <Link to="/settings?tab=branches" className="text-brand underline">
              Start one from a branch
            </Link>
            .
          </CardContent>
        </Card>
      ) : (
        <Card className="py-2">
          <CardContent className="flex flex-col gap-1 px-2">
            {requests.map((mr) => (
              <MergeRow key={mr.id} request={mr} />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function MergeRow({ request }: { request: MergeRequestListItem }) {
  const [closeMergeRequest, closeState] = useCloseMergeRequestMutation()
  const active = isMergeWorkActive(request.workStatus)
  const label = mergeRequestStatusLabel(request)
  const failed = request.workStatus === 'failed'

  const abandon = async () => {
    try {
      await closeMergeRequest(request.id).unwrap()
      toast.success('Merge abandoned')
    } catch (err) {
      toast.error(getApiErrorReason(err) === 'NOT_FOUND' ? 'Already gone' : 'Could not abandon')
    }
  }

  const mergeUrl = `/branches/merge?source=${encodeURIComponent(request.sourceId)}&target=${encodeURIComponent(request.targetId)}`

  return (
    <div className="flex items-center gap-2 rounded-md px-2 py-2 hover:bg-bg-sunk/40">
      <Link to={mergeUrl} className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-1.5 font-mono text-sm text-ink">
          <span className="truncate">{request.sourceName}</span>
          <GitMerge className="size-3.5 shrink-0 text-ink-3" />
          <span className="truncate">{request.targetName}</span>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-11 text-ink-3">
          <span
            className={`rounded-full px-1.5 py-0.5 font-medium ${STATUS_BADGE[label] ?? STATUS_BADGE.open}`}
          >
            {label}
          </span>
          <span>
            {request.resolved}/{request.total} resolved
          </span>
          <span>updated {formatRelativeTime(request.updatedAt)}</span>
          {request.createdBy ? <span>by {request.createdBy}</span> : null}
          {request.resultCommitHash ? (
            <span className="font-mono">{request.resultCommitHash.slice(0, 8)}</span>
          ) : null}
        </div>
        {failed && (request.workErrorMessage || request.workErrorReason) ? (
          <p className="mt-1 text-11 text-st-down">
            {request.workErrorMessage || request.workErrorReason}
          </p>
        ) : null}
      </Link>
      {request.status === 'open' ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={closeState.isLoading || active}
          onClick={() => void abandon()}
          className="shrink-0 text-st-down hover:bg-st-down-bg/60 hover:text-st-down"
        >
          Abandon
        </Button>
      ) : null}
    </div>
  )
}
